import React from 'react';
import { Link } from 'react-router-dom';
import { LayoutGrid } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@shared/ui/dropdown-menu';
import { Button } from '@shared/ui/button';
import { SuiteIcon } from '@shared/icons';
import { allSuites } from '@suites/registry';
import { useAuth } from '@shared/auth/AuthContext';
import { useActiveSuite } from '../useActiveSuite';
import { cn } from '@shared/lib/utils';

export const AppSwitcher: React.FC = () => {
  const { role, enabledSuites, hasPermission } = useAuth();
  const activeSuite = useActiveSuite();

  const permittedSuites = allSuites.filter((suite) => {
    if (enabledSuites && enabledSuites.length > 0 && !enabledSuites.includes(suite.id)) {
      return false;
    }
    if (suite.requiredPermission && !hasPermission(suite.requiredPermission)) {
      return false;
    }
    if (role === 'student' || role === 'parent') {
      return ['core', 'lms', 'sis', 'fees', 'communication'].includes(suite.id);
    }
    if (role === 'teacher') {
      return ['core', 'hr', 'lms', 'sis', 'scheduling', 'communication'].includes(suite.id);
    }
    if (role === 'counselor') {
      return ['core', 'crm', 'sis', 'scheduling', 'communication'].includes(suite.id);
    }
    if (role === 'accountant') {
      return ['core', 'fees', 'hr', 'bi'].includes(suite.id);
    }
    return true;
  });

  const viewLabel =
    role === 'student' || role === 'parent'
      ? 'Student Learning Suites'
      : role === 'teacher'
      ? 'Faculty & Teaching Suites'
      : 'Institutional Suites';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 text-navbar-muted hover:text-navbar-foreground hover:bg-navbar-accent shrink-0"
          aria-label="Apps dropdown"
          title="Switch Suite / Apps"
        >
          <LayoutGrid className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-[340px] p-2" sideOffset={8}>
        <div className="flex items-center justify-between px-2 py-1.5">
          <DropdownMenuLabel className="text-xs font-semibold uppercase tracking-wider text-muted-foreground p-0">
            {viewLabel}
          </DropdownMenuLabel>
          <span className="text-[10px] bg-primary/10 text-primary font-medium px-1.5 py-0.5 rounded">
            {permittedSuites.length} Suites Available
          </span>
        </div>
        <DropdownMenuSeparator className="my-1" />
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 p-1 max-h-[360px] overflow-y-auto">
          {permittedSuites.map((suite) => {
            const isActive = activeSuite?.id === suite.id;
            const isWebBuilder = suite.id === 'webbuilder';
            return (
              <DropdownMenuItem asChild key={suite.id} className="p-0 focus:bg-transparent">
                {isWebBuilder ? (
                  <a
                    href="/webbuilder/pages"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={cn(
                      'flex flex-col items-center justify-center gap-1.5 rounded-lg p-2.5 text-center transition-colors hover:bg-accent focus:bg-accent',
                      isActive && 'bg-accent/80 font-medium ring-1 ring-primary'
                    )}
                  >
                    <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
                      <SuiteIcon name={suite.icon} className="h-5 w-5" />
                    </div>
                    <span className="text-[11px] leading-tight line-clamp-2 text-foreground">
                      {suite.name}
                    </span>
                  </a>
                ) : (
                  <Link
                    to={suite.basePath}
                    className={cn(
                      'flex flex-col items-center justify-center gap-1.5 rounded-lg p-2.5 text-center transition-colors hover:bg-accent focus:bg-accent',
                      isActive && 'bg-accent/80 font-medium ring-1 ring-primary'
                    )}
                  >
                    <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
                      <SuiteIcon name={suite.icon} className="h-5 w-5" />
                    </div>
                    <span className="text-[11px] leading-tight line-clamp-2 text-foreground">
                      {suite.name}
                    </span>
                  </Link>
                )}
              </DropdownMenuItem>
            );
          })}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
